import { Component, Inject, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { FirebaseUtilitiesService } from '../../services/firebase-utilities.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  SharedFilesDialogComponent,
  SharedFilesDialogData
} from '../shared-files-dialog/shared-files-dialog.component';
import { ConfirmationDialogComponent } from '../ui/confirmation-dialog/confirmation-dialog.component';

export interface SharedUser {
  email: string;
  name: string;
  role?: string;
  fileCount?: number;
  sharedFiles?: any[];
  isOnline?: boolean;
  facilityName?: string;
}

export interface SharedUsersDialogData {
  fileName: string;
  fileId: string;
  casename: string;
  sharedUsers: SharedUser[];
  sharedDate?: Date;
}

@Component({
    selector: 'app-shared-users-dialog',
    templateUrl: './shared-users-dialog.component.html',
    styleUrls: ['./shared-users-dialog.component.scss'],
    standalone: false
})
export class SharedUsersDialogComponent implements OnInit {
  enhancedSharedUsers: SharedUser[] = [];
  loading = true;
  unsharing = false;
  unsharingAll = false;
  refreshing = false;

  constructor(
    public dialogRef: MatDialogRef<SharedUsersDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SharedUsersDialogData,
    private firebaseUtilitiesService: FirebaseUtilitiesService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.enhanceSharedUsersWithRolesAndFileCounts();
  }

  private async enhanceSharedUsersWithRolesAndFileCounts(): Promise<void> {
    try {
      this.loading = true;
      const enhancedUsers: SharedUser[] = [];

      // Get all facilities once to avoid multiple calls
      const facilities = await this.firebaseUtilitiesService.getFacilities();
      const facilityMap = new Map(facilities.map(f => [f.id, f.name]));

      for (const user of this.data.sharedUsers) {
        try {
          // Get user info and role
          const userInfo = await this.firebaseUtilitiesService.getUserInfoByEmail(user.email);

          // Get files shared with this user
          const sharedFiles = await this.firebaseUtilitiesService.getPatientSharedFilesByConsultant(
            this.data.casename,
            user.email
          );

          // Get facility name if user has a facility
          const facilityName = userInfo?.facility ? facilityMap.get(userInfo.facility) : undefined;

          enhancedUsers.push({
            email: user.email,
            name: user.name,
            role: userInfo?.role || 'Unknown',
            fileCount: sharedFiles.length,
            sharedFiles: sharedFiles,
            isOnline: this.checkUserOnlineStatus(user.email),
            facilityName: facilityName
          });
        } catch (error) {
          console.warn(`Could not fetch data for user ${user.email}:`, error);
          enhancedUsers.push({
            email: user.email,
            name: user.name,
            role: 'Unknown',
            fileCount: 0,
            sharedFiles: [],
            isOnline: false,
            facilityName: undefined
          });
        }
      }

      this.enhancedSharedUsers = enhancedUsers;
    } catch (error) {
      console.error('Error enhancing shared users with roles and file counts:', error);
      this.enhancedSharedUsers = this.data.sharedUsers.map(user => ({
        ...user,
        role: 'Unknown',
        fileCount: 0,
        sharedFiles: [],
        isOnline: false,
        facilityName: undefined
      }));
    } finally {
      this.loading = false;
    }
  }

  onClose(): void {
    this.dialogRef.close({
      action: 'closed',
      remainingUsers: this.data.sharedUsers
    });
  }

  async onRefreshData(): Promise<void> {
    if (this.refreshing) return;

    try {
      this.refreshing = true;
      await this.enhanceSharedUsersWithRolesAndFileCounts();
      this.snackBar.open('User data refreshed', 'Close', {
        duration: 2000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom'
      });
    } catch (error) {
      console.error('Error refreshing data:', error);
      this.snackBar.open('Failed to refresh data', 'Close', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom'
      });
    } finally {
      this.refreshing = false;
    }
  }

  onManageSharing(): void {
    // This could open a more comprehensive sharing management dialog
    this.snackBar.open('Sharing management feature coming soon', 'Close', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom'
    });
  }

  getRoleDisplayName(user: SharedUser): string {
    if (user.role && user.role !== 'Unknown') {
      // Handle special case for Client Admin role
      if (user.role.toLowerCase() === 'client admin') {
        return 'Facilitator';
      }
      return user.role.charAt(0).toUpperCase() + user.role.slice(1);
    }
    return 'User';
  }

  getRoleColor(user: SharedUser): string {
    if (!user.role || user.role === 'Unknown') {
      return '#757575';
    }

    switch (user.role.toLowerCase()) {
      case 'consultant':
        return '#f44336';
      case 'facility':
        return '#388e3c';
      case 'admin':
      case 'client admin':
        return '#f57c00';
      case 'owner':
        return '#d32f2f';
      case 'associate':
        return '#7b1fa2';
      default:
        return '#757575';
    }
  }

  async onUnshareFile(user: SharedUser): Promise<void> {
    if (this.unsharing) return;

    try {
      this.unsharing = true;

      // Create file object for unsharing
      const fileToUnshare = {
        fileId: this.data.fileId,
        fileName: this.data.fileName
      };

      // Unshare the file
      await this.firebaseUtilitiesService.unShareFile(fileToUnshare, user.email, this.data.casename);

      // Remove user from both local arrays
      this.enhancedSharedUsers = this.enhancedSharedUsers.filter(u => u.email !== user.email);
      this.data.sharedUsers = this.data.sharedUsers.filter(u => u.email !== user.email);

      // Notify parent component about the change
      this.dialogRef.close({
        action: 'unshared',
        user: user,
        remainingUsers: this.data.sharedUsers
      });

      this.snackBar.open(`File unshared with ${user.name}`, 'Close', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom'
      });
    } catch (error) {
      console.error('Error unsharing file:', error);
      this.snackBar.open('Failed to unshare file', 'Close', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom'
      });
    } finally {
      this.unsharing = false;
    }
  }

  async onUnshareAll(): Promise<void> {
    if (this.unsharingAll || this.enhancedSharedUsers.length === 0) return;

    // Show confirmation dialog
    const confirmDialog = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Unshare with All Users',
        message: `Are you sure you want to unshare "${this.data.fileName}" with all ${this.enhancedSharedUsers.length} users?`,
        btnOkText: 'Unshare All',
        btnCancelText: 'Cancel'
      }
    });

    const result = await firstValueFrom(confirmDialog.afterClosed());
    if (!result || !result.ok) return;

    try {
      this.unsharingAll = true;

      // Create file object for unsharing
      const fileToUnshare = {
        fileId: this.data.fileId,
        fileName: this.data.fileName
      };

      // Store user count before clearing arrays
      const userCount = this.enhancedSharedUsers.length;

      // Unshare with all users
      const unsharePromises = this.enhancedSharedUsers.map(user => 
        this.firebaseUtilitiesService.unShareFile(fileToUnshare, user.email, this.data.casename)
      );

      await Promise.all(unsharePromises);

      // Clear both local arrays
      this.enhancedSharedUsers = [];
      this.data.sharedUsers = [];

      // Notify parent component about the change
      this.dialogRef.close({
        action: 'unsharedAll',
        remainingUsers: []
      });

      this.snackBar.open(`File unshared with all ${userCount} users`, 'Close', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom'
      });
    } catch (error) {
      console.error('Error unsharing file with all users:', error);
      this.snackBar.open('Failed to unshare file with all users', 'Close', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom'
      });
    } finally {
      this.unsharingAll = false;
    }
  }

  onViewSharedFiles(user: SharedUser): void {
    if (user.sharedFiles && user.sharedFiles.length > 0) {
      this.dialog
        .open(SharedFilesDialogComponent, {
          width: '600px',
          maxHeight: '80vh',
          data: {
            userName: user.name,
            userEmail: user.email,
            files: user.sharedFiles,
            casename: this.data.casename
          } as SharedFilesDialogData
        })
        .afterClosed()
        .subscribe(result => {
          if (result && result.action === 'unshareAll') {
            this.onUnshareFile(user);
          }
        });
    }
  }

  private checkUserOnlineStatus(email: string): boolean {
    // This could be implemented to check actual online status
    // For now, return a random status for demonstration
    return Math.random() > 0.5;
  }

  // TrackBy function for better performance
  trackByUser(index: number, user: SharedUser): string {
    return user.email;
  }
}
